import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { closeModal } from "../../store/modal/modalReducer";
import { faTimes, faPaperPlane, faChevronDown, faChevronUp } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import axios from "axios";
import { toast } from "react-toastify";

export default function ModalInspectionImages({ inspection, images }) {
    const dispatch = useDispatch();
    const current_booking = useSelector((state) => state.booking.current_booking);
    
    const [selectedImages, setSelectedImages] = useState([]);
    const [showSendInterface, setShowSendInterface] = useState(false);
    const [message, setMessage] = useState(`Here are the ${inspection.type_label} inspection images for your review.`);
    const [sendMethod, setSendMethod] = useState('auto');
    const [sending, setSending] = useState(false);

    const toggleImageSelection = (imageId) => {
        setSelectedImages(prev => 
            prev.includes(imageId) 
                ? prev.filter(id => id !== imageId)
                : [...prev, imageId]
        );
    };

    const selectAllImages = () => {
        if (selectedImages.length === images.length) {
            setSelectedImages([]);
        } else {
            setSelectedImages(images.map(img => img.file_id));
        }
    };

    const handleSendImages = async () => {
        if (selectedImages.length === 0) {
            toast.error('Please select at least one image to send');
            return;
        }

        if (!message.trim()) {
            toast.error('Please enter a message');
            return;
        }

        setSending(true);

        try {
            const response = await axios.post(
                `${process.env.REACT_APP_API_URL}inspections/send-images`,
                {
                    booking_id: current_booking.id,
                    inspection_id: inspection.id,
                    image_ids: selectedImages,
                    message: message.trim(),
                    send_method: sendMethod
                }
            );

            if (response.data.success) {
                toast.success(
                    `Successfully sent ${response.data.image_count} image${response.data.image_count !== 1 ? 's' : ''} via ${response.data.send_method}`
                );
                
                // Reset state
                setSelectedImages([]);
                setShowSendInterface(false);
                setMessage(`Here are the ${inspection.type_label} inspection images for your review.`);
            } else {
                toast.error(response.data.message || 'Failed to send images');
            }
        } catch (error) {
            console.error('Error sending images:', error);
            const errorMessage = error.response?.data?.message || 'Failed to send images. Please try again.';
            toast.error(errorMessage);
        } finally {
            setSending(false);
        }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
            <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
                {/* Header */}
                <div className="flex justify-between items-center p-4 border-b">
                    <div>
                        <h2 className="text-xl font-bold text-gray-900">
                            {inspection.type_label} Inspection Images
                        </h2>
                        <p className="text-sm text-gray-600">
                            {inspection.created_at_human} • {images.length} image{images.length !== 1 ? 's' : ''}
                        </p>
                    </div>
                    <button
                        onClick={() => dispatch(closeModal())}
                        className="text-gray-500 hover:text-gray-700 p-2"
                    >
                        <FontAwesomeIcon icon={faTimes} size="lg" />
                    </button>
                </div>

                {/* Send to Tenant Button */}
                {images.length > 0 && (
                    <div className="px-4 pt-4">
                        <button
                            onClick={() => setShowSendInterface(!showSendInterface)}
                            className="w-full flex items-center justify-between px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
                            disabled={sending}
                        >
                            <span className="flex items-center gap-2">
                                <FontAwesomeIcon icon={faPaperPlane} />
                                Send to Tenant
                            </span>
                            <FontAwesomeIcon icon={showSendInterface ? faChevronUp : faChevronDown} />
                        </button>
                    </div>
                )}

                {/* Send Interface */}
                {showSendInterface && (
                    <div className="px-4 pt-4 pb-2 bg-blue-50 border-b border-blue-200">
                        <div className="space-y-3">
                            {/* Selection Info */}
                            <div className="flex items-center justify-between">
                                <p className="text-sm font-medium text-gray-700">
                                    Selected: {selectedImages.length} of {images.length} images
                                </p>
                                <button
                                    onClick={selectAllImages}
                                    className="text-xs px-2 py-1 bg-white border border-gray-300 rounded hover:bg-gray-50"
                                    disabled={sending}
                                >
                                    {selectedImages.length === images.length ? 'Deselect All' : 'Select All'}
                                </button>
                            </div>

                            {/* Message Input */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Message
                                </label>
                                <textarea
                                    value={message}
                                    onChange={(e) => setMessage(e.target.value)}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                                    rows="3"
                                    placeholder="Enter your message to the tenant..."
                                    disabled={sending}
                                    maxLength={2000}
                                />
                                <p className="text-xs text-gray-500 mt-1">
                                    {message.length}/2000 characters
                                </p>
                            </div>

                            {/* Send Method */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Send via
                                </label>
                                <div className="flex gap-4">
                                    <label className="flex items-center">
                                        <input
                                            type="radio"
                                            value="auto"
                                            checked={sendMethod === 'auto'}
                                            onChange={(e) => setSendMethod(e.target.value)}
                                            className="mr-2"
                                            disabled={sending}
                                        />
                                        <span className="text-sm">Auto (WhatsApp → Email)</span>
                                    </label>
                                    <label className="flex items-center">
                                        <input
                                            type="radio"
                                            value="whatsapp"
                                            checked={sendMethod === 'whatsapp'}
                                            onChange={(e) => setSendMethod(e.target.value)}
                                            className="mr-2"
                                            disabled={sending}
                                        />
                                        <span className="text-sm">WhatsApp</span>
                                    </label>
                                    <label className="flex items-center">
                                        <input
                                            type="radio"
                                            value="email"
                                            checked={sendMethod === 'email'}
                                            onChange={(e) => setSendMethod(e.target.value)}
                                            className="mr-2"
                                            disabled={sending}
                                        />
                                        <span className="text-sm">Email</span>
                                    </label>
                                </div>
                            </div>

                            {/* Action Buttons */}
                            <div className="flex gap-2 pt-2">
                                <button
                                    onClick={handleSendImages}
                                    disabled={sending || selectedImages.length === 0}
                                    className={`flex-1 px-4 py-2 rounded font-medium transition-colors ${
                                        sending || selectedImages.length === 0
                                            ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                                            : 'bg-green-600 text-white hover:bg-green-700'
                                    }`}
                                >
                                    {sending ? (
                                        <span className="flex items-center justify-center gap-2">
                                            <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                                            </svg>
                                            Sending...
                                        </span>
                                    ) : (
                                        <span className="flex items-center justify-center gap-2">
                                            <FontAwesomeIcon icon={faPaperPlane} />
                                            Send Images ({selectedImages.length})
                                        </span>
                                    )}
                                </button>
                                <button
                                    onClick={() => {
                                        setShowSendInterface(false);
                                        setSelectedImages([]);
                                    }}
                                    disabled={sending}
                                    className="px-4 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300 transition-colors disabled:opacity-50"
                                >
                                    Cancel
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {/* Images Grid */}
                <div className="flex-1 overflow-y-auto p-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {images.map((image, index) => {
                            const isSelected = selectedImages.includes(image.file_id);
                            return (
                                <div 
                                    key={index} 
                                    className={`relative border rounded-lg overflow-hidden shadow-sm hover:shadow-md transition-all ${
                                        isSelected ? 'ring-2 ring-blue-500 border-blue-500' : ''
                                    }`}
                                >
                                    {/* Selection Checkbox */}
                                    {showSendInterface && (
                                        <div 
                                            className="absolute top-2 left-2 z-10 bg-white rounded-md p-1 shadow-md cursor-pointer hover:bg-gray-50"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                if (!sending) {
                                                    toggleImageSelection(image.file_id);
                                                }
                                            }}
                                        >
                                            <input
                                                type="checkbox"
                                                checked={isSelected}
                                                onChange={() => {}}
                                                className="w-5 h-5 rounded border-gray-300 text-blue-600 focus:ring-blue-500 cursor-pointer pointer-events-none"
                                                disabled={sending}
                                            />
                                        </div>
                                    )}
                                    <div className="aspect-w-16 aspect-h-12 bg-gray-100">
                                        <img
                                            src={image.url}
                                            alt={image.name}
                                            className="w-full h-48 object-cover cursor-pointer"
                                            onClick={() => window.open(image.url, '_blank')}
                                        />
                                    </div>
                                    <div className="p-3 bg-white">
                                        <p className="text-sm font-semibold text-gray-800 mb-2">
                                            {image.category_name && (
                                                <span className="text-blue-600">{image.category_name}</span>
                                            )}
                                            {image.category_name && <span className="text-gray-400 mx-1">•</span>}
                                            <span>{image.item_name}</span>
                                            {image.option_label && (
                                                <>
                                                    <span className="text-gray-400 mx-1">•</span>
                                                    <span className="text-orange-600">{image.option_label}</span>
                                                </>
                                            )}
                                        </p>
                                        {image.created_at_human && (
                                            <p className="text-xs text-gray-500">
                                                📅 Uploaded {image.created_at_human}
                                            </p>
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                    </div>

                    {images.length === 0 && (
                        <div className="text-center py-12 text-gray-500">
                            <p>No images attached to this inspection</p>
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="border-t p-4 bg-gray-50">
                    <button
                        onClick={() => dispatch(closeModal())}
                        className="px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-700 transition-colors"
                        disabled={sending}
                    >
                        Close
                    </button>
                </div>
            </div>
        </div>
    );
}
